import dagre from 'dagre';

import { findLastIndex } from '../utils/array';
import { DbtDagNode } from './DagInterfaces';
import { DagreData, DagreResult } from './DagreTypes';
import { DagMap } from './InternalDagInterfaces';

export type GlobalCacheInput = {
  grain: string;
};

/**
 * Serialized DAG input which should force a re-layout if changed.
 */
export function dagreCacheKey<TDbtDagNode extends DbtDagNode>(
  dagMap: DagMap<TDbtDagNode>,
  globalCacheInput: GlobalCacheInput,
): string {
  const simplifiedMap: Record<string, any> = {};

  Object.entries(dagMap).forEach(
    ([key, { label, group, sublabel, access, dbtCloudProject, parents, type }]) => {
      // Should only be data which influences the sizing/layout of nodes
      simplifiedMap[key] = {
        label,
        group,
        sublabel,
        access,
        dbtCloudProject,
        parents,
        type,
      };
    },
  );

  return JSON.stringify(simplifiedMap) + JSON.stringify(globalCacheInput);
}

type Row = {
  x: number;
  id: string;
};

export type AccessibilityColumnEntry = {
  y: number;
  row: Row[];
};

export type AccessiblityGrid = {
  columns: AccessibilityColumnEntry[];
  positionMap: Record<string, { row: number; col: number }>;
};

/**
 * Sorts top to bottom, then left to right.
 */
export const nodePositionComparator = (
  a: { x: number; y: number },
  b: { x: number; y: number },
) => a.x - b.x || a.y - b.y;

export const generateAccessibilityGrid = (
  graph: dagre.graphlib.Graph<{}>,
): AccessiblityGrid => {
  const columns: AccessibilityColumnEntry[] = [];
  const nodes = graph
    .nodes()
    .map((nodeKey) => ({ ...graph.node(nodeKey), id: nodeKey }));
  nodes.sort?.(nodePositionComparator);

  const positionMap: Record<string, { row: number; col: number }> = {};

  nodes.forEach((node) => {
    const lastRow = columns.at(-1);
    const newRow = { x: node.x, id: node.id };
    let rowIndex = columns.length;
    let colIndex = 0;
    if (!lastRow || lastRow.y < node.y) {
      columns.push({ y: node.y, row: [newRow] });
    } else {
      rowIndex = findLastIndex(columns, (row) => row.y <= node.y);
      if (rowIndex === -1) {
        columns.unshift({ y: node.y, row: [newRow] });
      } else {
        colIndex = columns[rowIndex].row.length;
        columns[rowIndex]?.row.push(newRow);
      }
    }
    positionMap[node.id] = { row: rowIndex, col: colIndex };
  });

  return { columns, positionMap };
};

const DEFAULT_NODESEP = 20;

/**
 * With the longest-path ranker, isolated nodes (no parents and no children)
 * end up to the right of deeper nodes instead of the left. This runs after
 * dagre.layout() and shifts isolated nodes to the leftmost rank's x position,
 * then resolves any vertical overlaps.
 */
function shiftIsolatedNodesToLeftmostRank(
  graph: dagre.graphlib.Graph,
  edges: { startId: string; endId: string }[],
  nodesep: number,
): void {
  const hasIncoming = new Set<string>();
  const hasOutgoing = new Set<string>();
  for (const { startId, endId } of edges) {
    hasOutgoing.add(startId);
    hasIncoming.add(endId);
  }

  const allNodeKeys = graph.nodes();
  const isolatedKeys: string[] = [];
  let leftmostX = Infinity;

  for (const key of allNodeKeys) {
    const node = graph.node(key);
    if (node.x < leftmostX) leftmostX = node.x;
    if (!hasIncoming.has(key) && !hasOutgoing.has(key)) isolatedKeys.push(key);
  }

  if (isolatedKeys.length === 0) return;

  for (const key of isolatedKeys) {
    graph.node(key).x = leftmostX;
  }

  // Resolve vertical overlaps among all nodes now at leftmostX
  const nodesAtLeft: Array<{
    key: string;
    node: { x: number; y: number; height: number };
  }> = [];
  for (const key of allNodeKeys) {
    const node = graph.node(key);
    if (node.x === leftmostX) {
      nodesAtLeft.push({ key, node });
    }
  }

  nodesAtLeft.sort((a, b) => a.node.y - b.node.y);

  for (let i = 1; i < nodesAtLeft.length; i++) {
    const prev = nodesAtLeft[i - 1].node;
    const curr = nodesAtLeft[i].node;
    const minGap = prev.height / 2 + nodesep + curr.height / 2;
    if (curr.y - prev.y < minGap) {
      curr.y = prev.y + minGap;
    }
  }
}

export function calculateDagreLayout<TDbtDagNode extends DbtDagNode>({
  graphConfig,
  nodes,
  edges,
}: Omit<DagreData<TDbtDagNode>, ''>): DagreResult<TDbtDagNode> {
  const graph = new dagre.graphlib.Graph();
  graph.setGraph(graphConfig);
  graph.setDefaultEdgeLabel(function () {
    return {};
  });
  nodes.forEach(({ id, dagreNode }) => graph.setNode(id, dagreNode));
  edges.forEach(({ startId, endId }) => graph.setEdge(startId, endId));
  dagre.layout(graph);

  shiftIsolatedNodesToLeftmostRank(
    graph,
    edges,
    graphConfig.nodesep ?? DEFAULT_NODESEP,
  );

  const accessibilityGrid = generateAccessibilityGrid(graph);
  return {
    // @ts-ignore
    nodes: graph.nodes().map((nodeKey) => ({ ...graph.node(nodeKey), id: nodeKey })),
    edges: graph.edges(),
    wasCached: false,
    accessibilityGrid,
  };
}
